import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import CookieConsent from './components/CookieConsent';
import { trackPageview } from './lib/analytics';

// Scroll to top on every route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// GA4 pageview on every React Router navigation. Safe to call always —
// trackPageview() no-ops when the user hasn't consented.
const AnalyticsTracker: React.FC = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    trackPageview(pathname + search);
  }, [pathname, search]);
  return null;
};

// Layout components — keep eager (always needed)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ProtectedRoute from './client-portal/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// ─── LAZY LOAD everything else ───
// Public pages
const Home = React.lazy(() => import('./pages/HomePage'));
// Ad-landing pages (/lp/*) — one file per targeted audience, all sharing
// the same LandingPageTemplate for identical design language.
const SameDayVancouver         = React.lazy(() => import('./pages/lp/SameDayVancouver'));
const MedicalCourierVancouver  = React.lazy(() => import('./pages/lp/MedicalCourierVancouver'));
const BusinessCourierVancouver = React.lazy(() => import('./pages/lp/BusinessCourierVancouver'));
const UrgentHotshotVancouver   = React.lazy(() => import('./pages/lp/UrgentHotshotVancouver'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const ServiceDetailPage = React.lazy(() => import('./pages/ServiceDetailPage'));
const CoveragePage = React.lazy(() => import('./pages/CoveragePage'));
const CoverageDetailPage = React.lazy(() => import('./pages/CoverageDetailPage'));
const HowItWorksPage = React.lazy(() => import('./pages/HowItWorksPage'));
const BusinessPage = React.lazy(() => import('./pages/BusinessPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const OrderPage = React.lazy(() => import('./pages/OrderPage'));
const TrackPage = React.lazy(() => import('./pages/TrackPage'));
const WaybillDownloadPage = React.lazy(() => import('./pages/WaybillDownloadPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const PayInvoice = React.lazy(() => import('./pages/PayInvoice'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignUpPage = React.lazy(() => import('./pages/SignUpPage'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const BlogIndexPage = React.lazy(() => import('./pages/BlogIndexPage'));
const BlogArticlePage = React.lazy(() => import('./pages/BlogArticlePage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));

// Client portal
const ClientShell = React.lazy(() => import('./client-portal/ClientShell'));
const PortalDashboard = React.lazy(() => import('./client-portal/pages/Dashboard'));
const BookShipment = React.lazy(() => import('./client-portal/pages/BookShipment'));
const PortalOrders = React.lazy(() => import('./client-portal/pages/Orders'));
const OrderDetail = React.lazy(() => import('./client-portal/pages/OrderDetail'));
const Addresses = React.lazy(() => import('./client-portal/pages/Addresses'));
const Invoices = React.lazy(() => import('./client-portal/pages/Invoices'));
const Settings = React.lazy(() => import('./client-portal/pages/Settings'));
const Notifications = React.lazy(() => import('./client-portal/pages/Notifications'));

// Admin portal
const AdminShell = React.lazy(() => import('./admin-portal/AdminShell'));
const AdminDashboard = React.lazy(() => import('./admin-portal/pages/Dashboard'));
const AdminOrders = React.lazy(() => import('./admin-portal/pages/Orders'));
const AdminCreateOrder = React.lazy(() => import('./admin-portal/pages/CreateOrder'));
const AdminOrderDetail = React.lazy(() => import('./admin-portal/pages/OrderDetail'));
const AdminCustomers = React.lazy(() => import('./admin-portal/pages/Customers'));
const AdminCustomerDetail = React.lazy(() => import('./admin-portal/pages/CustomerDetail'));
const AdminDrivers = React.lazy(() => import('./admin-portal/pages/Drivers'));
const AdminPayouts = React.lazy(() => import('./admin-portal/pages/Payouts'));
const AdminDriverMap = React.lazy(() => import('./admin-portal/pages/DriverMap'));

// Driver mobile app (PWA — magic-link auth via /d/:token)
const DriverApp = React.lazy(() => import('./driver-portal/DriverApp'));
const DriverOrders = React.lazy(() => import('./driver-portal/pages/Orders'));
const DriverOrderDetail = React.lazy(() => import('./driver-portal/pages/OrderDetail'));
const DriverHistory = React.lazy(() => import('./driver-portal/pages/History'));
const DriverMyPay = React.lazy(() => import('./driver-portal/pages/MyPay'));
const AdminPricing = React.lazy(() => import('./admin-portal/pages/Pricing'));
const AdminInvoices = React.lazy(() => import('./admin-portal/pages/Invoices'));
const AdminReceivables = React.lazy(() => import('./admin-portal/pages/Receivables'));
const AdminTasks = React.lazy(() => import('./admin-portal/pages/Tasks'));
const AdminReports = React.lazy(() => import('./admin-portal/pages/Reports'));
const AdminSettings = React.lazy(() => import('./admin-portal/pages/Settings'));

// Loading fallback
const PageLoader: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <div style={{
      width: 36, height: 36,
      border: '3px solid #e5e7eb', borderTopColor: '#395ACB',
      borderRadius: '50%', animation: 'app-spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes app-spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);

const App: React.FC = () => {
  return (
    <ErrorBoundary>
    <Router>
      <ScrollToTop />
      <AnalyticsTracker />
      <CookieConsent />
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ─── PUBLIC WEBSITE ─── (includes ad landing pages, which use
                 the same Header + Footer to match site branding) */}
            <Route element={<PublicLayout />}>
              <Route path="/lp/same-day-vancouver"         element={<SameDayVancouver />} />
              <Route path="/lp/medical-courier-vancouver"  element={<MedicalCourierVancouver />} />
              <Route path="/lp/business-courier-vancouver" element={<BusinessCourierVancouver />} />
              <Route path="/lp/urgent-hotshot-vancouver"   element={<UrgentHotshotVancouver />} />
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<ServiceDetailPage />} />
              <Route path="/coverage" element={<CoveragePage />} />
              <Route path="/coverage/:slug" element={<CoverageDetailPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/business" element={<BusinessPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/order" element={<OrderPage />} />
              <Route path="/track" element={<TrackPage />} />
              <Route path="/track/:orderNumber" element={<TrackPage />} />
              <Route path="/waybill/:orderNumber" element={<WaybillDownloadPage />} />
              <Route path="/pay/:invoiceNumber" element={<PayInvoice />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* ─── CLIENT PORTAL (authenticated) ─── */}
            <Route element={<ProtectedRoute />}>
              <Route path="/portal" element={<ClientShell />}>
                <Route index element={<PortalDashboard />} />
                <Route path="book" element={<BookShipment />} />
                <Route path="orders" element={<PortalOrders />} />
                <Route path="orders/:orderId" element={<OrderDetail />} />
                <Route path="addresses" element={<Addresses />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="settings" element={<Settings />} />
                <Route path="notifications" element={<Notifications />} />
              </Route>
            </Route>

            {/* ─── DRIVER PORTAL (PWA, magic-link auth) ─── */}
            <Route path="/d/:token" element={<DriverApp />}>
              <Route index element={<DriverOrders />} />
              <Route path="history" element={<DriverHistory />} />
              <Route path="pay" element={<DriverMyPay />} />
              <Route path="orders/:orderId" element={<DriverOrderDetail />} />
            </Route>

            {/* ─── ADMIN PORTAL ─── */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/new" element={<AdminCreateOrder />} />
              <Route path="orders/:orderId" element={<AdminOrderDetail />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customers/:customerId" element={<AdminCustomerDetail />} />
              <Route path="drivers" element={<AdminDrivers />} />
              <Route path="payouts" element={<AdminPayouts />} />
              <Route path="driver-map" element={<AdminDriverMap />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="receivables" element={<AdminReceivables />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Routes>
        </Suspense>
        <Toaster position="top-right" />
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
};

// Public pages use Header + Footer. Skip-to-content link stays visually
// hidden until keyboard focus lands on it (WCAG 2.4.1 requirement — lets
// screen-reader + keyboard users bypass the nav).
const PublicLayout: React.FC = () => (
  <div className="min-h-screen flex flex-col">
    <a
      href="#main-content"
      style={{
        position: 'absolute',
        left: 8,
        top: 8,
        background: '#1E2547',
        color: '#fff',
        padding: '8px 16px',
        borderRadius: 6,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 700,
        zIndex: 1000,
        transform: 'translateY(-200%)',
        transition: 'transform 0.2s',
      }}
      onFocus={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
      onBlur={(e) => { e.currentTarget.style.transform = 'translateY(-200%)'; }}
    >
      Skip to main content
    </a>
    <Header />
    <main id="main-content" className="flex-grow"><Outlet /></main>
    <Footer />
  </div>
);

export default App;
